import { localStorage } from '#app/services/storage.js';
import { parseJson } from '#app/utilities/parseJson.js';
import { captureException } from '@contra/sentry/react';
import { z } from 'zod';

const VisitorSessionInputsZodSchema = z.object({
  firstReferrerUrl: z
    .string()
    .url()
    .nullable()
    .transform((value) => {
      return value || null;
    }),
  firstUtmTag: z
    .object({
      campaign: z.string(),
      content: z.string().nullable(),
      medium: z.string(),
      source: z.string(),
      term: z.string().nullable(),
    })
    .nullable()
    .transform((value) => {
      if (value === null) {
        return null;
      }

      return {
        utmCampaign: value.campaign,
        utmContent: value.content,
        utmMedium: value.medium,
        utmSource: value.source,
        utmTerm: value.term,
      };
    }),
  firstVisitedUrl: z.string(),
  visitedUrls: z.array(z.string().url()),
});

type Input = {
  firstReferrerUrl: null | string;
  firstUtmTag: null | {
    campaign: string;
    content: null | string;
    medium: string;
    source: string;
    term: null | string;
  };
  firstVisitedUrl: null | string;
  visitedUrls: string[];
};

const deleteVisitorSessionInputs = () => {
  localStorage.removeItem('CONTRA_LOCAL_FIRST_REFERRER_URL');
  localStorage.removeItem('CONTRA_LOCAL_FIRST_UTM_TAG');
  localStorage.removeItem('CONTRA_LOCAL_FIRST_VISITED_URL');
  localStorage.removeItem('CONTRA_LOCAL_RECENT_VISITED_URLS');
};

/**
 * The purpose of this is to obtain values recorded by guide.js script.
 */
export const findVisitorSessionInputs = () => {
  // If guide.js script is not loaded (such as locally),
  // then CONTRA_LOCAL_FIRST_VISITED_URL is not set.
  if (!localStorage.getItem('CONTRA_LOCAL_FIRST_VISITED_URL')) {
    return null;
  }

  let input: Input | null = null;

  try {
    const maybeFirstReferrerUrl = localStorage.getItem(
      'CONTRA_LOCAL_FIRST_REFERRER_URL',
    );

    const maybeFirstVisitedUrl = localStorage.getItem(
      'CONTRA_LOCAL_FIRST_VISITED_URL',
    );

    input = {
      firstReferrerUrl: maybeFirstReferrerUrl
        ? maybeFirstReferrerUrl === 'null'
          ? null
          : parseJson(maybeFirstReferrerUrl)
        : null,
      firstUtmTag: localStorage.getItem('CONTRA_LOCAL_FIRST_UTM_TAG')
        ? parseJson(
            localStorage.getItem('CONTRA_LOCAL_FIRST_UTM_TAG') ?? '"null"',
          )
        : null,
      firstVisitedUrl: maybeFirstVisitedUrl
        ? maybeFirstVisitedUrl === 'null'
          ? null
          : parseJson(maybeFirstVisitedUrl)
        : null,
      visitedUrls: localStorage.getItem('CONTRA_LOCAL_RECENT_VISITED_URLS')
        ? parseJson(
            localStorage.getItem('CONTRA_LOCAL_RECENT_VISITED_URLS') ?? '[]',
          )
        : [],
    };
  } catch (error) {
    captureException(error);

    deleteVisitorSessionInputs();

    return null;
  }

  const result = VisitorSessionInputsZodSchema.safeParse(input);

  if (!result.success) {
    captureException(result.error, {
      extra: {
        input,
      },
    });

    // eslint-disable-next-line no-console
    console.log(input);

    // eslint-disable-next-line no-console
    console.error(result.error);

    deleteVisitorSessionInputs();

    return null;
  }

  return result.data;
};
